import React, { Suspense } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import KeyboardArrowDownRounded from '@mui/icons-material/KeyboardArrowDownRounded';
import MailOutlineRounded from '@mui/icons-material/MailOutlineRounded';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

import Reveal from './Reveal';
import { Eyebrow } from './Section';
import { contactPath } from '../seo/routes';
import { azure, emerald, gradientText, line, slate } from '../theme';
import MsPartner from '../assets/img/mspartner.png';

// three.js + react-globe.gl live in their own chunk; the hero paints instantly.
const ParticleEarth = React.lazy(() => import('./ParticleEarth'));

/** True where a mouse can actually grab the globe. */
function useCanDrag() {
  const [fine, setFine] = React.useState(true);
  React.useEffect(() => {
    const mq = window.matchMedia('(pointer: fine)');
    const apply = () => setFine(mq.matches);
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);
  return fine;
}

function supportsWebGL() {
  try {
    const c = document.createElement('canvas');
    const gl = c.getContext('webgl') || c.getContext('experimental-webgl');
    const ok = !!(window.WebGLRenderingContext && gl);
    // Release the probe context immediately: it counts against the browser's
    // live-context cap (tightest on iOS) until GC gets around to the canvas.
    if (gl) {
      const lose = gl.getExtension('WEBGL_lose_context');
      if (lose) lose.loseContext();
    }
    return ok;
  } catch {
    return false;
  }
}

function scrollToSection(id) {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

export default function Hero() {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const canDrag = useCanDrag();
  const webglOK = React.useMemo(supportsWebGL, []);

  const stats = [
    { value: t('hero.stat1v'), label: t('hero.stat1l') },
    { value: t('hero.stat2v'), label: t('hero.stat2l') },
    { value: t('hero.stat3v'), label: t('hero.stat3l') },
  ];

  const trustedNames = [
    t('clients.subtitlelink4'),
    t('clients.subtitlelink2'),
    t('clients.subtitlelink3'),
    t('clients.subtitlelink1'),
  ];

  return (
    <Box
      component="section"
      id="home"
      sx={{
        position: 'relative',
        minHeight: '100svh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        overflow: 'hidden',
        pt: { xs: 14, md: 10 },
        pb: { xs: 0, md: 6 },
        background: `
          radial-gradient(1100px 560px at 82% 18%, ${alpha(azure[500], 0.14)}, transparent 62%),
          radial-gradient(820px 480px at 8% 92%, ${alpha(emerald[400], 0.07)}, transparent 60%)
        `,
        // faint engineering-grid dots, fading toward the fold
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          backgroundImage: `radial-gradient(${alpha(slate[400], 0.13)} 1px, transparent 1px)`,
          backgroundSize: '34px 34px',
          maskImage: 'linear-gradient(to bottom, black 0%, transparent 88%)',
          WebkitMaskImage: 'linear-gradient(to bottom, black 0%, transparent 88%)',
          pointerEvents: 'none',
        },
      }}
    >
      {/* Content column. pointerEvents none/auto pair: the Container is a
          full-width block layered OVER the globe, and it was swallowing every
          drag on the globe's left half — only the inner content box should
          catch the pointer. */}
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2, pointerEvents: 'none' }}>
        <Box sx={{ maxWidth: { xs: '100%', md: 640 }, pointerEvents: 'auto' }}>
          <Reveal>
            <Eyebrow>{t('hero.eyebrow')}</Eyebrow>
          </Reveal>

          <Reveal delay={80}>
            <Typography variant="h1" component="h1">
              {t('hero.titleA')}{' '}
              <Box component="span" sx={gradientText}>
                {t('hero.titleB')}
              </Box>
            </Typography>
          </Reveal>

          <Reveal delay={160}>
            <Typography variant="subtitle1" sx={{ mt: 2.5, maxWidth: 560, color: 'text.secondary' }}>
              {t('hero.lead')}
            </Typography>
          </Reveal>

          <Reveal delay={240}>
            <Stack direction="row" spacing={2} sx={{ mt: 4, flexWrap: 'wrap', rowGap: 2 }}>
              <Button
                variant="contained"
                size="large"
                component={RouterLink}
                to={contactPath(i18n.language)}
                startIcon={<MailOutlineRounded />}
              >
                {t('hero.ctaPrimary')}
              </Button>
              <Button variant="outlined" size="large" onClick={() => scrollToSection('services')}>
                {t('hero.ctaSecondary')}
              </Button>
            </Stack>
          </Reveal>

          <Reveal delay={320}>
            <Stack
              direction="row"
              sx={{ mt: { xs: 4.5, md: 5 }, flexWrap: 'wrap', rowGap: 3 }}
              divider={
                <Box
                  sx={{
                    width: '1px',
                    alignSelf: 'stretch',
                    bgcolor: line,
                    mx: { xs: 2.5, sm: 3.5 },
                  }}
                />
              }
            >
              {stats.map((s) => (
                <Box key={s.label}>
                  <Typography
                    component="p"
                    sx={{ fontSize: '2rem', fontWeight: 800, lineHeight: 1.1, letterSpacing: '-0.02em' }}
                  >
                    {s.value}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5, color: 'text.secondary', maxWidth: 150 }}>
                    {s.label}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Reveal>

          <Reveal delay={400}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={2.5}
              sx={{ mt: { xs: 4.5, md: 5 }, flexWrap: 'wrap', rowGap: 2 }}
            >
              <Box
                component="img"
                src={MsPartner}
                alt="Microsoft Partner"
                sx={{ height: 42, width: 'auto', opacity: 0.95 }}
              />
              <Box sx={{ width: '1px', alignSelf: 'stretch', bgcolor: line }} />
              <Box>
                <Typography
                  variant="overline"
                  component="p"
                  sx={{ color: 'text.secondary', lineHeight: 1.6 }}
                >
                  {t('hero.trust')}
                </Typography>
                <Typography variant="body2" sx={{ color: slate[300], fontWeight: 700 }}>
                  {trustedNames.join('  ·  ')}
                </Typography>
              </Box>
            </Stack>
          </Reveal>
        </Box>
      </Container>

      {/*
        Globe: DESKTOP ONLY, and not rendered at all below md.

        On a phone it was a 380px band of near-black with a floating caption in
        it. The sphere sat almost entirely outside its own box, so what a
        visitor actually got was empty space where a globe had been promised,
        and a hint inviting a drag that the page could not afford to accept
        (capturing the swipe stopped the page scrolling). Shrinking it, pulling
        the camera in and thinning the labels were all tried; a 390px column in
        a dark theme is simply not where this belongs.

        So it is a desktop flourish. isDesktop gates the RENDER, not just the
        styling, which also spares a phone the WebGL context, the three.js
        bundle and the battery it was spending on a decoration nobody could see.
      */}
      <Box
        sx={{
          position: { xs: 'relative', md: 'absolute' },
          insetBlockStart: { md: 0 },
          insetBlockEnd: { md: 0 },
          // The off-canvas bleed softens as the viewport grows: -13% at md
          // pushed city labels off the edge on ordinary 1440px desktops.
          insetInlineEnd: { md: '-13%', lg: '-8%', xl: '-4%' },
          width: { xs: '100%', md: '58%' },
          height: { xs: 380, sm: 460, md: 'auto' },
          mt: { xs: 3, md: 0 },
          zIndex: 1,
          // At md widths the globe's inboard edge slides under the copy;
          // fading it keeps the text readable without shrinking the globe.
          maskImage: {
            md: 'linear-gradient(to right, transparent 0%, black 24%)',
            lg: 'none',
          },
          WebkitMaskImage: {
            md: 'linear-gradient(to right, transparent 0%, black 24%)',
            lg: 'none',
          },
        }}
      >
        {webglOK ? (
          <Suspense fallback={<div className="dc-globe-placeholder" />}>
            <ParticleEarth density={isDesktop ? 1 : 0.85} />
          </Suspense>
        ) : (
          <div className="dc-globe-placeholder" />
        )}

        {/* Shown on mobile too — the globe is interactive there as well now. */}
        <Typography
          variant="caption"
          sx={{
            position: 'absolute',
            bottom: '7%',
            insetInlineStart: '50%',
            transform: 'translateX(-50%)',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 1,
            px: 1.75,
            py: 0.5,
            borderRadius: 999,
            color: 'text.secondary',
            bgcolor: alpha('#0A101F', 0.6),
            border: `1px solid ${line}`,
            backdropFilter: 'blur(6px)',
            pointerEvents: 'none',
            whiteSpace: 'nowrap',
          }}
        >
          <Box
            component="span"
            sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              bgcolor: emerald[400],
              boxShadow: `0 0 8px 1px ${alpha(emerald[400], 0.8)}`,
            }}
          />
          {canDrag ? t('hero.globeHint') : t('hero.globeHintStatic')}
        </Typography>
      </Box>

      {/* Scroll indicator */}
      <Box
        onClick={() => scrollToSection('services')}
        role="presentation"
        className="dc-scroll-hint"
        sx={{
          position: 'absolute',
          bottom: 22,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 2,
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          alignItems: 'center',
          color: 'text.secondary',
          cursor: 'pointer',
          '&:hover': { color: 'text.primary' },
        }}
      >
        <Typography variant="caption" sx={{ letterSpacing: '0.14em', textTransform: 'uppercase' }}>
          {t('hero.scroll')}
        </Typography>
        <KeyboardArrowDownRounded fontSize="small" />
      </Box>
    </Box>
  );
}
